import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CdkAccordionItem, CdkAccordionModule } from '@angular/cdk/accordion';
import { NgClass, NgIf, ViewportScroller } from '@angular/common';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { RouterLink } from '@angular/router';
import { buildProductDescriptionSections } from '../../common/build-product-description-sections';
import { GetProductDetailQuery } from '../../common/gql/graphql';
import { qualifiesForLetterboxService } from '../../common/qualifies-for-letterbox-service';

@Component({
    selector: 'kb-product-accordion',
    templateUrl: './product-accordion.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CdkAccordionModule, NgClass, FaIconComponent, NgIf, RouterLink],
})
export class ProductAccordionComponent implements OnChanges {
    @Input() keyFeatures?: string;
    @Input() description: string;
    @Input() facetValues: NonNullable<GetProductDetailQuery['product']>['facetValues'];
    @Input() variants: NonNullable<GetProductDetailQuery['product']>['variants'] = [];
    @Input() selectedVariantId: string | null;
    protected readonly deliveryReturnsTitle = 'Delivery & Returns';

    protected readonly chevronDown = faChevronDown;
    sections: Array<{
        title: string;
        content: string;
        specs?: Array<{ name: string; values: string[] }>;
    }> = [];
    private readonly facetCodesToOmitFromSpecs = [
        'new-arrival',
        'gift',
        'sale',
        'can-be-used-with',
        'beginners',
        'category',
    ];
    isLargeLetterShipping = false;

    constructor(private viewportScroller: ViewportScroller) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['variants']) {
            if (changes.variants.previousValue?.[0]?.id !== changes.variants.currentValue?.[0]?.id) {
                this.sections = [];
            }
        }
        if (!this.sections.length) {
            this.sections = buildProductDescriptionSections({
                text: this.description,
                keyFeatures: this.keyFeatures,
                descriptionCutoffLength: 5000,
            });
        }
        if (this.sections.length && !this.sections.find((section) => section.title === 'Specifications')) {
            const specs = this.buildSpecs(this.facetValues ?? []);
            if (specs.length) {
                this.sections.push({
                    title: 'Specifications',
                    content: '',
                    specs,
                });
            }
        }
        if (
            this.sections.length &&
            !this.sections.find((section) => section.title === this.deliveryReturnsTitle)
        ) {
            this.sections.push({
                title: this.deliveryReturnsTitle,
                content: '',
            });
        }
        this.sections.sort((a, b) => {
            // ensure the deliveryReturnsTitle is always last
            if (a.title === this.deliveryReturnsTitle) {
                return 1;
            }
            if (b.title === this.deliveryReturnsTitle) {
                return -1;
            }
            return 0;
        });
        this.isLargeLetterShipping = qualifiesForLetterboxService(this.variants ?? [], this.selectedVariantId);
    }

    protected accordionItemClick(accordionItem: CdkAccordionItem, anchor: string) {
        accordionItem.toggle();
        setTimeout(() => {
            if (!this.isInViewport(anchor)) {
                this.viewportScroller.scrollToAnchor(anchor);
            }
        }, 0);
    }

    private buildSpecs(facetValues: NonNullable<GetProductDetailQuery['product']>['facetValues']) {
        const valuesMap = new Map<string, string[]>();
        const selectedVariant = this.variants?.find((v) => v.id === this.selectedVariantId);
        if (selectedVariant) {
            valuesMap.set('Product SKU', [selectedVariant.sku]);
            if(selectedVariant.customFields?.gtin) {
                valuesMap.set('GTIN', [selectedVariant.customFields.gtin]);
            }
        }
        const includedFacets = facetValues.filter(
            (facetValue) => !this.facetCodesToOmitFromSpecs.includes(facetValue.facet.code),
        );
        includedFacets.forEach((facetValue) => {
            if (valuesMap.has(facetValue.facet.name)) {
                valuesMap.get(facetValue.facet.name).push(facetValue.name);
            } else {
                valuesMap.set(facetValue.facet.name, [facetValue.name]);
            }
        });
        return Array.from(valuesMap.entries()).map(([name, values]) => ({ name, values }));
    }

    private isInViewport(elementId: string): boolean {
        const element = document.getElementById(elementId);

        if (!element) {
            return true;
        }

        const rect = element.getBoundingClientRect();

        // Check if the element is within the viewport's bounds
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }
}
